import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/firebase';
import { ChatAttachment } from '@/types/chat';

// Supported file types
export const SUPPORTED_TYPES = {
  IMAGE: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  DOCUMENT: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'text/csv'
  ],
  VIDEO: ['video/mp4', 'video/avi', 'video/mov'],
  AUDIO: ['audio/mp3', 'audio/wav', 'audio/aac', 'audio/m4a']
};

// File size limits (in bytes)
export const FILE_SIZE_LIMITS = {
  IMAGE: 10 * 1024 * 1024, // 10MB
  DOCUMENT: 25 * 1024 * 1024, // 25MB
  VIDEO: 100 * 1024 * 1024, // 100MB
  AUDIO: 50 * 1024 * 1024 // 50MB
};

// Upload file to Firebase Storage
export const uploadFile = async (
  file: File,
  userId: string,
  threadId: string
): Promise<ChatAttachment> => {
  try {
    // Validate file type
    if (!isFileTypeSupported(file.type, file.name)) {
      throw new Error(`Unsupported file type: ${file.type}`);
    }

    // Validate file size
    const maxSize = getMaxFileSize(file.type);
    if (file.size > maxSize) {
      throw new Error(`File too large. Maximum size: ${(maxSize / 1024 / 1024).toFixed(1)}MB`);
    }

    // Create storage reference
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storageRef = ref(storage, `chat-attachments/${userId}/${threadId}/${fileName}`);

    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Determine attachment type
    const attachmentType = determineFileType(file.type, file.name);

    // Create attachment object
    const attachment: ChatAttachment = {
      id: `att_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
      type: attachmentType,
      url: downloadURL,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type
    };

    return attachment;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

// Check if file type is supported
const isFileTypeSupported = (mimeType: string, fileName: string): boolean => {
  // Check MIME type first
  if (SUPPORTED_TYPES.IMAGE.includes(mimeType) || 
      SUPPORTED_TYPES.VIDEO.includes(mimeType) || 
      SUPPORTED_TYPES.AUDIO.includes(mimeType) || 
      SUPPORTED_TYPES.DOCUMENT.includes(mimeType)) {
    return true;
  }
  
  // Fallback: check file extension
  const extension = fileName.toLowerCase().split('.').pop();
  if (!extension) return false;
  
  const supportedExtensions = {
    IMAGE: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    VIDEO: ['mp4', 'avi', 'mov'],
    AUDIO: ['mp3', 'wav', 'aac', 'm4a'],
    DOCUMENT: ['pdf', 'doc', 'docx', 'txt', 'csv']
  };
  
  return supportedExtensions.IMAGE.includes(extension) ||
         supportedExtensions.VIDEO.includes(extension) ||
         supportedExtensions.AUDIO.includes(extension) ||
         supportedExtensions.DOCUMENT.includes(extension);
};

// Get maximum file size for file type
const getMaxFileSize = (mimeType: string): number => {
  if (SUPPORTED_TYPES.IMAGE.includes(mimeType)) return FILE_SIZE_LIMITS.IMAGE;
  if (SUPPORTED_TYPES.VIDEO.includes(mimeType)) return FILE_SIZE_LIMITS.VIDEO;
  if (SUPPORTED_TYPES.AUDIO.includes(mimeType)) return FILE_SIZE_LIMITS.AUDIO;
  return FILE_SIZE_LIMITS.DOCUMENT;
};

// Determine file type from MIME type or extension
const determineFileType = (mimeType: string, fileName: string): 'image' | 'document' | 'video' | 'audio' => {
  // Check MIME type first
  if (SUPPORTED_TYPES.IMAGE.includes(mimeType)) return 'image';
  if (SUPPORTED_TYPES.VIDEO.includes(mimeType)) return 'video';
  if (SUPPORTED_TYPES.AUDIO.includes(mimeType)) return 'audio';
  
  // Fallback to file extension
  const extension = fileName.toLowerCase().split('.').pop();
  if (!extension) return 'document';
  
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) return 'image';
  if (['mp4', 'avi', 'mov'].includes(extension)) return 'video';
  if (['mp3', 'wav', 'aac', 'm4a'].includes(extension)) return 'audio';
  
  return 'document';
};

// Format file size for display
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Get file icon based on type
export const getFileIcon = (mimeType: string): string => {
  if (SUPPORTED_TYPES.IMAGE.includes(mimeType)) return '🖼️';
  if (SUPPORTED_TYPES.VIDEO.includes(mimeType)) return '🎥';
  if (SUPPORTED_TYPES.AUDIO.includes(mimeType)) return '🎵';
  return '📄';
};
